import React, { lazy, Suspense, useEffect } from "react"
import { css } from "@emotion/react"
import { Redirect, Switch, useHistory } from "react-router-dom"
import { useAuth, UserId } from "@ncs/ncs-api"
import {
	ClientUpdater,
	faIcons as legosFaIcons,
	Toaster,
	Flag,
	useIsFeatureFlagOn,
} from "@ncs/web-legos"
import {
	ContentWrapperEcomm,
	HelpFab,
	Route,
	SiteHeaderEcomm,
	NotFoundPage,
} from "~/shared-components"
import SiteFooter from "./shared-components/site-footer/SiteFooter"
import { ShopEcomm } from "~/views"
import { CheckoutEcommPage } from "./views/shop/checkout/CheckoutEcommPage"
import { CartSummaryEcomm } from "./views/shop/cart-summary/CartSummaryEcomm"
import { CartSummary } from "./views/shop/cart-summary/CartSummary"
import { PartSearchResultsEcomm } from "./views/shop/part-search-results/PartSearchResultsEcomm"
import { PartSearchResults } from "./views/shop/part-search-results/PartSearchResults"
import { PartDetailEcomm } from "./views/shop/part-detail/PartDetailEcomm"
import { PartDetail } from "./views/shop/part-detail/PartDetail"
import { useAnalytics } from "./hooks/useAnalytics"

const AccountPage = lazy(() =>
	import("~/views/account/AccountPage").then((module) => ({ default: module.AccountPage }))
)

const BlueWaveWelcome = lazy(() =>
	import("~/views/blue-wave-welcome/BlueWaveWelcome").then((module) => ({
		default: module.BlueWaveWelcome,
	}))
)
const ConnectedSiteDetail = lazy(() =>
	import("~/views/connectivity/connected-site-detail").then((module) => ({
		default: module.ConnectedSiteDetail,
	}))
)

const ConnectedSites = lazy(() =>
	import("~/views/connectivity/connected-sites").then((module) => ({
		default: module.ConnectedSites,
	}))
)
const CreateAccount = lazy(() =>
	import("~/views/auth/create-account").then((module) => ({
		default: module.CreateAccount,
	}))
)
const Dashboard = lazy(() =>
	import("~/views/dashboard/Dashboard").then((module) => ({
		default: module.Dashboard,
	}))
)
const ForgotPassword = lazy(() =>
	import("~/views/auth/forgot-password/ForgotPassword").then((module) => ({
		default: module.ForgotPassword,
	}))
)

const Login = lazy(() =>
	import("~/views/auth/login").then((module) => ({
		default: module.Login,
	}))
)

const Logout = lazy(() =>
	import("~/views/auth/logout/Logout").then((module) => ({
		default: module.Logout,
	}))
)

const PunchOut = lazy(() =>
	import("~/views/punch-out").then((module) => ({ default: module.PunchOut }))
)

const PunchoutOrderFinished = lazy(() =>
	import("~/views/shop/punchout-order-finished").then((module) => ({
		default: module.PunchoutOrderFinished,
	}))
)

const MyEquipment = lazy(() =>
	import("~/views/my-equipment/").then((module) => ({ default: module.MyEquipment }))
)

const QuickOrder = lazy(() =>
	import("~/views/shop/quick-order/QuickOrder").then((module) => ({
		default: module.QuickOrder,
	}))
)
const ResetPassword = lazy(() =>
	import("~/views/auth/reset-password").then((module) => ({
		default: module.ResetPassword,
	}))
)
const ServiceRequests = lazy(() =>
	import("~/views/service-requests").then((module) => ({
		default: module.ServiceRequests,
	}))
)

const App: React.FC = () => {
	const history = useHistory()
	const loginWallAllowAnonymous = true
	const isEcommServiceAPIEnabled =
		useIsFeatureFlagOn(Flag.ECOMM_SERVICE_API) && !localStorage["Legacy"]

	const { identify, trackCustomerPortalNavigation, fetchExperiments, startExperiment } =
		useAnalytics()

	useEffect(() => {
		const unlisten = history.listen((location, event: string) => {
			trackCustomerPortalNavigation(location.pathname)
		})

		return () => {
			unlisten()
		}
	}, [history, trackCustomerPortalNavigation])

	const { user, punchOutSessionId, initialized } = useAuth()

	const userData = user ? JSON.stringify(user) : null

	useEffect(() => {
		startExperiment()
	}, [startExperiment])

	useEffect(() => {
		if (userData && initialized) {
			const userDataParsed = JSON.parse(userData)
			identify({
				id: userDataParsed.id,
				email: userDataParsed.email,
				name: userDataParsed.name,
				punchOutSessionId: punchOutSessionId ?? null,
			})
		}
		fetchExperiments()
	}, [userData, punchOutSessionId, initialized, identify, fetchExperiments])

	return (
		<div css={appContainer}>
			<ClientUpdater />
			<SiteHeaderEcomm />
			<main id="app-main" css={main}>
				<ContentWrapperEcomm>
					<Suspense fallback={<div />}>
						<Switch>
							{/* For now, the homepage / dashboard will just redirect to the shop. */}
							<Redirect exact from="/" to="/shop" />

							{/* Authentication routes */}
							<Route
								allowAnonymous
								redirectIds={UserId.DrivenBrands}
								path="/auth/forgot-password"
								component={ForgotPassword}
							/>
							<Route
								allowAnonymous
								redirectIds={UserId.DrivenBrands}
								path="/auth/reset-password/:token"
								component={ResetPassword}
							/>
							<Route
								allowAnonymous
								redirectIfAuthenticated
								exact
								path="/auth/login"
								component={Login}
							/>
							<Route allowAnonymous exact path="/auth/logout" component={Logout} />
							<Route
								allowAnonymous
								redirectIfAuthenticated
								exact
								path="/auth/create-account"
								component={CreateAccount}
							/>

							{/* Punchout pages */}
							<Route allowAnonymous exact path="/punch-out" component={PunchOut} />
							<Route
								allowAnonymous
								exact
								path="/shop/punchout-order-finished"
								component={PunchoutOrderFinished}
							/>

							{/* BlueWave pages */}
							<Route exact path="/blue-wave" component={BlueWaveWelcome} />

							{/* The rest of the website */}
							<Route allowAnonymous={true} path="/dashboard" component={Dashboard} />
							<Route
								allowAnonymous={loginWallAllowAnonymous}
								exact
								path="/shop"
								component={ShopEcomm}
							/>

							{isEcommServiceAPIEnabled ?
								<Route
									allowAnonymous={loginWallAllowAnonymous}
									exact
									path="/shop/search"
									component={PartSearchResults}
								/>
							:	<Route
									allowAnonymous={loginWallAllowAnonymous}
									exact
									path="/shop/search"
									component={PartSearchResultsEcomm}
								/>
							}

							{isEcommServiceAPIEnabled ?
								<Route
									allowAnonymous={loginWallAllowAnonymous}
									exact
									path="/shop/parts/:partId"
									component={PartDetail}
								/>
							:	<Route
									allowAnonymous={loginWallAllowAnonymous}
									exact
									path="/shop/parts/:partId"
									component={PartDetailEcomm}
								/>
							}

							{isEcommServiceAPIEnabled ?
								<Route
									allowAnonymous={loginWallAllowAnonymous}
									exact
									path="/shop/cart-summary"
									component={CartSummary}
								/>
							:	<Route
									allowAnonymous={loginWallAllowAnonymous}
									exact
									path="/shop/cart-summary"
									component={CartSummaryEcomm}
								/>
							}

							<Route exact path="/shop/checkout" component={CheckoutEcommPage} />
							<Route exact path="/shop/quick-order" component={QuickOrder} />
							<Route exact path="/connectivity" component={ConnectedSites} />
							<Route
								exact
								path="/connectivity/:siteId"
								component={ConnectedSiteDetail}
							/>
							<Route
								path="/service-requests"
								redirectIds={UserId.DrivenBrands}
								component={ServiceRequests}
							/>
							<Route
								path="/my-equipment"
								redirectIds={UserId.DrivenBrands}
								component={MyEquipment}
							/>
							<Route
								path="/account"
								redirectIds={UserId.DrivenBrands}
								component={AccountPage}
							/>
							<Route component={NotFoundPage} />
						</Switch>
					</Suspense>
				</ContentWrapperEcomm>
			</main>
			<SiteFooter />
			<Toaster />
			<HelpFab />
		</div>
	)
}

export default App

const appContainer = css`
	display: flex;
	flex-direction: column;
	min-height: 100vh;
`

const main = css`
	flex: 1;
	background-color: #f9fafb;
	padding-bottom: 8rem;
`
